// redux/slices/chatSlice.js (WEB)
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { resetStore } from "./resetStore";
import { apiRequest } from "../../Utils/apiClient";
import { getSocket, connectWithUid } from "../../Utils/socket";

/* ========================= HELPERS ========================= */

const getError = (error, fallback) =>
  error?.response?.data ||
  error?.message ||
  fallback;

const getReadySocket = async () => {
  let s = getSocket();
  if (!s || !s.connected) {
    await connectWithUid();
    s = getSocket();
  }
  return s;
};

const mid = (m) => m?.messageId ?? m?.id;

const ADMIN_UID = "123456789";

/* ========================= THUNKS ========================= */

export const fetchAllChats = createAsyncThunk(
  "chat/fetchAllChats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest("GET", "/chat/allMessages");
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(getError(error, "fetchAllChats failed"));
    }
  }
);

export const updateMessagesAsRead = createAsyncThunk(
  "chat/updateMessagesAsRead",
  async ({ chatPartnerId, isAdmin }, { rejectWithValue }) => {
    try {
      await apiRequest("POST", "/chat/markAsRead", { chatPartnerId, isAdmin });
      return { chatPartnerId, isAdmin };
    } catch (error) {
      return rejectWithValue(getError(error, "updateMessagesAsRead failed"));
    }
  }
);

export const fetchPaginatedMessages = createAsyncThunk(
  "chat/fetchPaginatedMessages",
  async ({ senderId, receiverId, offset = 0, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(
        "GET",
        `/chat/messages?senderId=${senderId}&receiverId=${receiverId}&offset=${offset}&limit=${limit}`
      );
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(getError(error, "fetchPaginatedMessages failed"));
    }
  }
);

export const emitMarkAsRead = createAsyncThunk(
  "chat/emitMarkAsRead",
  async ({ chatPartnerId, isAdmin }, { rejectWithValue }) => {
    try {
      const s = await getReadySocket();
      s.emit("markAsRead", { chatPartnerId, isAdmin });
      return { chatPartnerId, isAdmin };
    } catch (e) {
      return rejectWithValue(e?.message || "socket emit failed");
    }
  }
);

/* История через socket — как в RN */
export const fetchChatHistory = (senderId, receiverId) => async (dispatch, getState) => {
  if (getState().chat.isHistoryFetched) return;
  const s = await getReadySocket();
  s.emit("getChatHistory", { senderId, receiverId });
  s.once("chatHistory", (messages) => {
    const unique = Array.from(new Set(messages.map((m) => JSON.stringify(m)))).map((m) =>
      JSON.parse(m)
    );
    dispatch(setChatHistory(unique));
  });
};

/* Слушатели socket — вызывать один раз при монтировании виджета */
export const setupWebSocketListener = () => async (dispatch) => {
  const s = await getReadySocket();
  if (!s) return;

  s.off("newMessage");
  s.off("messagesMarkedAsRead");
  s.off("messageEdited");
  s.off("reactionUpdated");

  s.on("newMessage", (message) => dispatch(addNewMessage(message)));

  s.on("messagesMarkedAsRead", ({ chatPartnerId, isAdmin }) => {
    dispatch(markMessagesAsRead({ chatPartnerId, isAdmin }));
  });

  s.on("messageEdited", ({ messageId, newText }) => {
    dispatch(applyMessageEdit({ messageId, newText }));
  });

  s.on("reactionUpdated", ({ messageId, reactions }) => {
    dispatch(applyReaction({ messageId, reactions }));
  });
};

/* Онлайн-статусы — REST снапшот + socket-подписка */
export const setupOnlineListener = createAsyncThunk(
  "chat/setupOnlineListener",
  async (_, { dispatch }) => {
    try {
      const response = await apiRequest("GET", "/chat/online");
      const { userIds } = response?.data || response || {};
      if (userIds) dispatch(setOnlineUsers({ userIds }));
    } catch (e) {
      console.warn("[chatSlice] /chat/online failed:", e?.message);
    }

    const s = await getReadySocket();
    if (!s) return;

    s.off("onlineUsers");
    s.off("userOnline");
    s.off("userOffline");

    s.on("onlineUsers", ({ userIds }) => dispatch(setOnlineUsers({ userIds })));
    s.on("userOnline", ({ userId }) => dispatch(setUserOnline({ userId })));
    s.on("userOffline", ({ userId }) => dispatch(setUserOffline({ userId })));
  }
);

/* ========================= STATE ========================= */

const initialState = {
  messages: [],
  unreadCounts: {},
  isHistoryFetched: false,
  hasMore: true,
  isLoadingMore: false,
  offset: 0,
  limit: 100,
  onlineMap: {},
};

/* ========================= SLICE ========================= */

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    addNewMessage: (state, action) => {
      const exists = state.messages.some((m) => mid(m) === mid(action.payload));
      if (!exists) state.messages.push(action.payload);
    },

    setChatHistory: (state, action) => {
      state.messages = action.payload;
      state.isHistoryFetched = true;
    },

    markMessagesAsRead: (state, action) => {
      const { chatPartnerId, isAdmin } = action.payload || {};
      if (!chatPartnerId && chatPartnerId !== 0) return;
      state.messages = state.messages.map((msg) => {
        if (isAdmin && msg.senderId === chatPartnerId && msg.receiverId === ADMIN_UID)
          return { ...msg, isReadByAdmin: true, isRead: true };
        if (!isAdmin && msg.senderId === ADMIN_UID && msg.receiverId === chatPartnerId)
          return { ...msg, isReadByMaster: true, isRead: true };
        return msg;
      });
      state.unreadCounts[chatPartnerId] = 0;
    },

    applyMessageEdit: (state, action) => {
      const { messageId, newText } = action.payload;
      const idx = state.messages.findIndex((m) => m.id === messageId || m.messageId === messageId);
      if (idx !== -1) {
        state.messages[idx] = { ...state.messages[idx], message: newText, isEdited: true };
      }
    },

    /* payload: { messageId, reactions: [{ userId, emoji }] } */
    applyReaction: (state, action) => {
      const { messageId, reactions } = action.payload;
      const idx = state.messages.findIndex((m) => m.id === messageId || m.messageId === messageId);
      if (idx !== -1) {
        state.messages[idx] = { ...state.messages[idx], reactions };
      }
    },

    setOnlineUsers: (state, action) => {
      const map = {};
      (action.payload.userIds || []).forEach((uid) => {
        map[uid] = true;
      });
      state.onlineMap = map;
    },

    setUserOnline: (state, action) => {
      state.onlineMap[action.payload.userId] = true;
    },

    setUserOffline: (state, action) => {
      delete state.onlineMap[action.payload.userId];
    },

    appendMessages: (state, action) => {
      const newMessages = action.payload || [];
      state.messages = [...newMessages, ...state.messages];
      state.offset += newMessages.length;
      state.hasMore = newMessages.length === state.limit;
    },

    clearMessages: (state) => {
      state.messages = [];
      state.hasMore = true;
      state.isHistoryFetched = false;
      state.offset = 0;
    },

    resetChat: (state) => {
      state.messages = [];
      state.offset = 0;
      state.hasMore = true;
      state.isHistoryFetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllChats.fulfilled, (state, action) => {
        const list = action.payload || [];
        state.messages = list;
        state.unreadCounts = list.reduce((acc, msg) => {
          if (!msg.isRead) {
            const id = msg.senderId !== ADMIN_UID ? msg.senderId : msg.receiverId;
            acc[id] = (acc[id] || 0) + 1;
          }
          return acc;
        }, {});
      })

      .addCase(updateMessagesAsRead.fulfilled, (state, action) => {
        const { chatPartnerId, isAdmin } = action.payload || {};
        state.messages = state.messages.map((msg) => {
          if (isAdmin && msg.senderId === chatPartnerId && msg.receiverId === ADMIN_UID)
            return { ...msg, isReadByAdmin: true, isRead: true };
          if (!isAdmin && msg.senderId === ADMIN_UID && msg.receiverId === chatPartnerId)
            return { ...msg, isReadByMaster: true, isRead: true };
          return msg;
        });
        state.unreadCounts[chatPartnerId] = 0;
      })

      .addCase(fetchPaginatedMessages.pending, (state) => {
        state.isLoadingMore = true;
      })
      .addCase(fetchPaginatedMessages.fulfilled, (state, action) => {
        const newMessages = action.payload || [];
        state.messages = [...newMessages, ...state.messages];
        state.offset += newMessages.length;
        state.hasMore = newMessages.length === state.limit;
        state.isLoadingMore = false;
      })
      .addCase(fetchPaginatedMessages.rejected, (state) => {
        state.isLoadingMore = false;
      })

      .addCase(emitMarkAsRead.fulfilled, () => {})

      .addCase(resetStore, () => initialState);
  },
});

/* ========================= ACTIONS ========================= */

export const {
  addNewMessage,
  setChatHistory,
  markMessagesAsRead,
  applyMessageEdit,
  applyReaction,
  setOnlineUsers,
  setUserOnline,
  setUserOffline,
  appendMessages,
  clearMessages,
  resetChat,
} = chatSlice.actions;

export default chatSlice.reducer;
